import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Loading } from "@/components/shared";
import {
  Users, Clock, AlertTriangle, GraduationCap, Calendar, IndianRupee, UserX,
  BookOpen, UserCheck, TrendingUp,
} from "lucide-react";
import { money } from "@/lib/api";
import MyDay from "@/components/MyDay";
import PeriodStats from "@/components/PeriodStats";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"], queryFn: async () => (await api.get("/reports/dashboard")).data,
    refetchInterval: 30000,
  });
  const { data: onboarding } = useQuery({
    queryKey: ["onboarding"], queryFn: async () => (await api.get("/settings/onboarding")).data,
    enabled: user?.role === "admin",
  });

  if (isLoading) return <Loading />;
  const role = data?.role;

  return (
    <div>
      <PageHeader title={`Namaste, ${user?.name?.split(" ")[0] || "there"} 👋`}
        subtitle={`Here's your ${role === "admin" ? "academy" : "work"} snapshot for today`} testid="dashboard-header" />

      {user?.role === "admin" && onboarding && onboarding.completed < onboarding.total && (
        <div className="mb-6 rounded-xl border border-orange-200 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-900 p-5" data-testid="onboarding-banner">
          <div className="flex items-center justify-between mb-3">
            <div className="font-head font-bold text-orange-900 dark:text-orange-200">Finish setting up LaunchX ({onboarding.completed}/{onboarding.total})</div>
            <button className="text-sm text-orange-700 font-semibold" onClick={() => navigate("/settings")}>Go to Settings →</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {onboarding.checklist.map((c) => (
              <button key={c.key} onClick={() => navigate(c.link)}
                className={`text-xs px-3 py-1.5 rounded-full border ${c.done ? "bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-950/40" : "bg-white border-orange-300 text-orange-800 dark:bg-slate-800"}`}>
                {c.done ? "✓ " : c.status === "setup_required" ? "⚙ " : "○ "}{c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {(role === "admin" || role === "sales") && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <StatCard label="New leads" value={data.new_leads} icon={Users} tone="blue" onClick={() => navigate("/leads")} testid="stat-new-leads" />
          <StatCard label="Today's callbacks" value={data.today_callbacks} icon={Clock} tone="orange" onClick={() => navigate("/follow-ups")} testid="stat-today-callbacks" />
          <StatCard label="Overdue follow-ups" value={data.overdue} icon={AlertTriangle} tone="red" onClick={() => navigate("/follow-ups")} testid="stat-overdue" />
          <StatCard label="Missing next action" value={data.missing_next_action} icon={UserX} tone="slate" onClick={() => navigate("/follow-ups")} testid="stat-missing" />
          <StatCard label="Admission pending" value={data.admission_pending} icon={GraduationCap} tone="orange" onClick={() => navigate("/admissions")} testid="stat-adm-pending" />
          <StatCard label="Visits today" value={data.visits_today} icon={Calendar} tone="blue" onClick={() => navigate("/appointments")} testid="stat-visits" />
          {role === "admin" && <StatCard label="Unassigned leads" value={data.unassigned} icon={UserX} tone="red" onClick={() => navigate("/leads")} testid="stat-unassigned" />}
          {role === "admin" && <StatCard label="Verified collections" value={money(data.collections_verified)} icon={IndianRupee} tone="green" onClick={() => navigate("/fees")} testid="stat-collections" />}
        </div>
      )}

      {role === "reception" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Appointments today" value={data.appointments_today} icon={Calendar} tone="blue" onClick={() => navigate("/appointments")} testid="stat-appts" />
          <StatCard label="Walk-ins" value={data.walkins} icon={Users} tone="orange" onClick={() => navigate("/appointments")} testid="stat-walkins" />
          <StatCard label="Check-ins" value={data.checkins} icon={UserCheck} tone="green" testid="stat-checkins" />
          <StatCard label="Pending payments" value={data.pending_payments} icon={IndianRupee} tone="red" onClick={() => navigate("/fees")} testid="stat-pending-pay" />
        </div>
      )}

      {role === "marketing" && (
        <div className="space-y-6" data-testid="marketing-dashboard">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Leads today" value={data.leads_today} icon={Users} tone="blue" testid="stat-mkt-today" />
            <StatCard label="Leads this month" value={data.leads_month} icon={Users} tone="orange" testid="stat-mkt-month" />
            <StatCard label="Admissions this month" value={data.admissions_month} icon={UserCheck} tone="green" onClick={() => navigate("/marketing")} testid="stat-mkt-adm" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[["Leads by source (this month)", data.leads_by_source, "mkt-leads-src"], ["Admissions by source (this month)", data.admissions_by_source, "mkt-adm-src"]].map(([title, rows, tid]) => (
              <div key={tid} className="rounded-xl border bg-white dark:bg-slate-800 p-4" data-testid={tid}>
                <div className="font-semibold mb-3">{title}</div>
                {!(rows || []).length && <p className="text-sm text-slate-400">No data yet</p>}
                {(rows || []).map((r) => (
                  <div key={r.source} className="flex justify-between text-sm py-1.5 border-b last:border-0"><span className="capitalize">{r.source}</span><b>{r.count}</b></div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {role === "trainer" && (
        <div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="My batches" value={data.batches} icon={BookOpen} tone="blue" onClick={() => navigate("/batches")} testid="stat-batches" />
            <StatCard label="My students" value={data.students} icon={UserCheck} tone="green" onClick={() => navigate("/students")} testid="stat-students" />
          </div>
          <div className="grid gap-3">
            {(data.my_batches || []).map((b) => (
              <div key={b.id} className="p-4 rounded-xl border bg-white dark:bg-slate-800 flex items-center justify-between">
                <div><div className="font-semibold">{b.name}</div><div className="text-sm text-slate-500">{b.schedule}</div></div>
                <button className="text-brand-blue text-sm font-semibold" onClick={() => navigate("/students")}>View roster →</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {role === "admin" && data.stage_counts && (
        <div className="mt-6 p-5 rounded-xl border bg-white dark:bg-slate-800">
          <div className="flex items-center gap-2 mb-4 font-head font-bold"><TrendingUp className="w-5 h-5 text-brand-blue" /> Pipeline overview</div>
          <div className="flex gap-3 overflow-x-auto stage-scroll pb-2">
            {Object.entries(data.stage_counts).map(([st, c]) => (
              <div key={st} className="min-w-[120px] p-3 rounded-lg bg-slate-50 dark:bg-slate-900 border text-center">
                <div className="text-xl font-extrabold">{c}</div>
                <div className="text-[11px] text-slate-500 mt-1">{st}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {(role === "admin" || role === "sales" || role === "reception") && <MyDay />}
      {(role === "admin" || role === "sales" || role === "reception") && <PeriodStats />}
    </div>
  );
}
